import { HeaderOverlayProvider, useHeaderOverlay } from '../lib/HeaderOverlayContext';
import { useEffect, useLayoutEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useMatch } from "react-router-dom";
import { DeferredEntityPopoverHost } from "./EntityPopoverContext";
import { AskStarmapLauncher } from "./AskStarmapLauncher";
import { ToolsMenu } from "./ToolsMenu";
import { UserMenu } from "./UserMenu";
import { ActiveRaterProvider } from "../lib/ActiveRaterContext";
import { OrgChartNavigationProvider } from "../lib/OrgChartNavigationContext";
import { OrgSearchProvider, useOrgSearch } from "../lib/OrgSearchContext";
import { WorkspaceProvider, useWorkspace } from "../lib/WorkspaceContext";
import { useWorkspacePath } from "../lib/workspaceRoutes";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { DEPLOYMENT_LABEL } from "../lib/deployment-environment";

/**
 * Authenticated shell: product identity left, workspace search center,
 * tools and user/admin controls right. Styled by prototype.css.
 */
export function Shell() {
  return (
    <WorkspaceProvider>
      <HeaderOverlayProvider><ShellInner /></HeaderOverlayProvider>
    </WorkspaceProvider>
  );
}

function ShellInner() {
  const { workspace } = useWorkspace();
  const workspaceTo = useWorkspacePath();
  const { pathname } = useLocation();
  const admin = pathname.startsWith(workspaceTo('/admin'));
  const reviewMatch = useMatch('/app/:workspaceId/admin/ingestions/:id');
  const reviewId = reviewMatch?.params.workspaceId === workspace.id ? reviewMatch.params.id : undefined;
  const prioritizeReview = Boolean(reviewId);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!reviewId) return;
    let current = true;
    // Reuse the same identity check and cache, without waiting for the admin screen to render.
    void queryClient.fetchQuery({
      queryKey: ['me', workspace.id],
      queryFn: () => api.me(workspace.id),
      staleTime: 5 * 60_000,
    }).then(me => {
      if (current && me.isAdmin) return queryClient.prefetchQuery({
        queryKey: ['ingestion', workspace.id, reviewId],
        queryFn: () => api.getIngestion(reviewId, workspace.id),
      });
    }).catch(() => {});
    return () => { current = false; };
  }, [queryClient, reviewId, workspace.id]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <ActiveRaterProvider prioritizeReview={prioritizeReview}>
      <OrgSearchProvider>
        <OrgChartNavigationProvider>
          <div className="starmap-shell min-h-screen flex flex-col">
            <a
              href="#main-content"
              className="fixed left-4 top-3 z-[300] -translate-y-20 rounded-md bg-[var(--color-brand-text)] px-4 py-2 text-sm font-semibold text-[var(--color-brand-bg)] transition-transform focus:translate-y-0"
            >
              Skip to content
            </a>
            <header className="shell-header sticky top-0 z-[200]">
              <div className="hdr !static">
                <div className="hdr-l hdr-l-inner">
                  <Link to={workspaceTo()} aria-label="Starmap home" className="hdr-logo">
                    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                      <rect x=".5" y=".5" width="37" height="37" rx="4.5" fill="#0D1A16" stroke="rgba(199,243,107,.34)" />
                      <ellipse cx="19" cy="19" rx="12" ry="5.8" stroke="#C7F36B" strokeWidth="1.25" transform="rotate(-28 19 19)" />
                      <ellipse cx="19" cy="19" rx="12" ry="5.8" stroke="#F4F7F5" strokeOpacity=".28" strokeWidth="1" transform="rotate(34 19 19)" />
                      <circle cx="19" cy="19" r="3.4" fill="#C7F36B" />
                      <circle cx="19" cy="19" r="1.15" fill="#0D1A16" />
                      <circle cx="27.5" cy="12.7" r="1.5" fill="#F4F7F5" />
                      <circle cx="9.7" cy="22.4" r="1" fill="#C7F36B" />
                    </svg>
                  </Link>
                  <div className="hdr-copy">
                    <div className="title fbar-row">
                      <span className="hdr-brand">STARMAP</span>
                      <span className="hdr-dash">-</span>
                      <span className="hdr-tagline">{workspace.name.toUpperCase()} {workspace.tagline.toUpperCase()}</span>
                    </div>
                    <div className="sub">{DEPLOYMENT_LABEL}{workspace.status === 'pilot' ? ' · Pilot' : ''} · Caylent Internal</div>
                  </div>
                </div>
                <div className="hdr-c">
                  {admin ? <Link to={workspaceTo()} className="text-sm text-white/80 hover:underline">Back to Starmap</Link> : <HeaderSearch />}
                </div>
                <div className="hdr-r flex items-center gap-2">
                  <WorkspaceSwitcher prioritizeReview={prioritizeReview} />
                  {!admin && <ToolsMenu />}
                  <UserMenu />
                </div>
              </div>
              {!admin && <PrimaryNav />}
            </header>

            <main id="main-content" tabIndex={-1} className="flex-1 outline-none">
              <Outlet />
            </main>

            <DeferredEntityPopoverHost />
            {!admin && <AskStarmapLauncher />}
          </div>
        </OrgChartNavigationProvider>
      </OrgSearchProvider>
    </ActiveRaterProvider>
  );
}

const PRIMARY_DESTINATIONS = [
  { label: 'Organization', path: '' },
  { label: 'Ask', path: '/ask' },
  { label: 'Paths', path: '/paths' },
  { label: 'Coverage', path: '/coverage' },
  { label: 'Manager', path: '/manager' },
  { label: 'Town', path: '/town' },
] as const;

function PrimaryNav() {
  const workspaceTo = useWorkspacePath();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    setCanScrollLeft(scroller.scrollLeft > 1);
    setCanScrollRight(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1);
  };

  useLayoutEffect(() => {
    updateScroll();
    window.addEventListener('resize', updateScroll);
    return () => window.removeEventListener('resize', updateScroll);
  }, []);

  const scrollNav = (direction: -1 | 1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({ left: direction * Math.max(180, scroller.clientWidth - 48), behavior: 'auto' });
  };

  return (
    <nav
      aria-label="Primary"
      className="border-b border-white/10 bg-[#0f172a]/98 px-3 shadow-[0_12px_28px_-24px_rgba(0,0,0,0.9)] sm:px-5"
    >
      <div className="primary-nav__row">
        <button
          type="button"
          className="primary-nav__step"
          aria-label="Scroll primary navigation left"
          disabled={!canScrollLeft}
          onClick={() => scrollNav(-1)}
        >‹</button>
        <div ref={scrollerRef} onScroll={updateScroll} className="primary-nav__scroller flex gap-1 overflow-x-auto py-1.5 [scrollbar-width:none]">
          {PRIMARY_DESTINATIONS.map((destination) => (
            <NavLink
              key={destination.path}
              to={workspaceTo(destination.path)}
              end={destination.path === ''}
              className={({ isActive }) =>
                `shrink-0 rounded-md px-3 py-2 text-xs leading-4 font-semibold tracking-[0.04em] transition-colors ${
                  isActive
                    ? 'bg-[#f8fafc]/15 text-[#f8fafc]'
                    : 'text-[#cbd5e1] hover:bg-[#f8fafc]/[0.08] hover:text-[#f8fafc]'
                }`
              }
            >
              {destination.label}
            </NavLink>
          ))}
        </div>
        <button
          type="button"
          className="primary-nav__step"
          aria-label="Scroll primary navigation right"
          disabled={!canScrollRight}
          onClick={() => scrollNav(1)}
        >›</button>
      </div>
    </nav>
  );
}

function HeaderSearch() {
  const { query, setQuery, clearQuery } = useOrgSearch();
  const [searchOpen, setSearchOpen] = useHeaderOverlay('search');
  const searchRef = useRef<HTMLInputElement>(null);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const workspaceTo = useWorkspacePath();
  const location = useLocation();
  const navigate = useNavigate();
  const orgPath = workspaceTo();
  const onOrgPath = location.pathname === orgPath;

  const updateQuery = (next: string) => {
    setQuery(next);
    if (next.trim() && !onOrgPath) navigate(orgPath);
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => updateQuery(e.target.value);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      clearQuery();
      e.currentTarget.blur();
      setSearchOpen(false);
      searchTriggerRef.current?.focus();
    }
    if (e.key === 'Enter' && !onOrgPath) navigate(orgPath);
  };

  return (
    <div className="header-search-control" data-open={searchOpen}>
      <button ref={searchTriggerRef} type="button" className="header-search-toggle" aria-label="Search Starmap" aria-expanded={searchOpen} onClick={() => {
        setSearchOpen(value => !value);
        if (!searchOpen) requestAnimationFrame(() => {
          if (document.activeElement === searchTriggerRef.current) searchRef.current?.focus();
        });
      }}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden><path d="M8.75 15.5a6.75 6.75 0 1 1 0-13.5 6.75 6.75 0 0 1 0 13.5ZM14 14l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
      </button>
      <label className="hdr-search" aria-label="Search Starmap">
      <svg width="16" height="16" viewBox="0 0 20 20" className="hdr-search-icon" fill="none" aria-hidden>
        <path
          d="M8.75 15.5a6.75 6.75 0 1 1 0-13.5 6.75 6.75 0 0 1 0 13.5ZM14 14l4 4"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
      <input
        type="search"
        onFocus={() => setSearchOpen(true)}
        ref={searchRef}
        value={query}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder="Search people, accounts, roles..."
        className="hdr-search-input [&::-webkit-search-cancel-button]:hidden"
      />
      {query && (
        <button type="button" onClick={clearQuery} className="hdr-search-clear" aria-label="Clear search">
          ×
        </button>
      )}
      </label>
    </div>
  );
}
